import {
  Button,
  Flex,
  Badge,
  Input,
  InputGroup,
  InputLeftElement,
  Tooltip,
} from '@chakra-ui/react'
import React, { useState, useMemo, useEffect } from 'react'
import CustomTabs from '../../../app/components/CustomTabs/CustomTabs'
import Header from '../../../app/components/Header'
import Device from '../components/Device'
import { DEFAULTPERIOD, PERIODPARAMS } from '../../dashboard/consts'
import { useQueryParams } from '../../dashboard/hooks/useQueryParams'
import FilterIcon from '../../../assets/icons/Filter'
import FilterModal from '../components/FilterModal'
import RegisterAutomationModal from '../components/RegisterAutomation'
import Search from '../../../assets/icons/Search'
import { debounce } from '../../../utils/helpers'
import Groups from '../components/Groups'
import DeviceAutomations from '../components/DeviceAutomations'
import GroupAutomations from '../components/GroupAutomations'
import { TableIcon } from '../../../assets/icons/TableIcon'
import {
  useCreateDeviceAutomationMutation,
  useUpdateDeviceAutomationMutation,
  useCreateGroupAutomationMutation,
} from '../api/automationsApi'
import { useLocation } from 'react-router-dom'
import { useDeviceStatusContext } from '../../../context/DeviceStatusContext'
import { disconnectSocket, initiateSocketConnection, subscribeToMessages } from '../../Layout/ws/layoutWs'

const Automations = () => {
  const [index, setIndex] = useState(0)
  const [detailsActive, setDetailsActive] = useState(false)
  const [sourceTab, setSourceTab] = useState<number | null>(null)
  const [groupId, setGroupId] = useState(null)
  const [deviceId, setDeviceId] = useState(null)
  const [locationId, setLocationId] = useState(null)
  const [deviceAutomation, setDeviceAutomation] = useState(null)
  const [deviceAutomationParam, setDeviceAutomationParam] = useState(null)
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [searchInput, setSearchInput] = useState<string | undefined>(undefined)
  const [hasAutomation, setHasAutomation] = useState(false)
  const [createDeviceAutomation] = useCreateDeviceAutomationMutation()
  const [updateDeviceAutomation] = useUpdateDeviceAutomationMutation()
  const [createGroupAutomation] = useCreateGroupAutomationMutation()
  const location = useLocation()
  const { setDeviceStatusData } = useDeviceStatusContext()
  useEffect(() => {
    const token = window.localStorage.getItem('aiot_t')
    const socketServerURL = `${process.env.REACT_APP_WS_URL}/ws/switch-status/?token=${token}`

    initiateSocketConnection(socketServerURL, token)
    const messageHandler = (message: any) => {
      if (typeof message === 'string') {
        try {
          const parsedMessage = JSON.parse(message)
          if (parsedMessage) {

            setDeviceStatusData((prev) => {
              const updated = [...prev]

              for (const incoming of parsedMessage) {
                const index = updated.findIndex(
                  (item) => item.id === incoming.id && item.parameter === incoming.parameter
                )

                if (index !== -1) {
                  updated[index] = incoming
                } else {
                  updated.push(incoming)
                }
              }

              return updated
            })
          }
        } catch (e) {
          console.error('Error parsing message:', e)
        }

      } else if (Array.isArray(message)) {
        setDeviceStatusData((prev) => {
          const updated = [...prev]

          for (const incoming of message) {
            const index = updated.findIndex(
              (item) => item.id === incoming.id && item.parameter === incoming.parameter
            )

            if (index !== -1) {
              updated[index] = incoming
            } else {
              updated.push(incoming)
            }
          }

          return updated
        })

      }
    }
    subscribeToMessages(messageHandler)
    return () => {
      disconnectSocket()
    }
  }, [location.pathname])
  const [queryParams] = useQueryParams(PERIODPARAMS)
  const querySearchParams = { ...DEFAULTPERIOD, ...queryParams }

  const filtersCounter = useMemo(() => {
    let appliedFilters = 0
    if (locationId) {
      appliedFilters += 1
    }

    if (groupId) {
      appliedFilters += 1
    }

    if (deviceId) {
      appliedFilters += 1
    }
    if (hasAutomation) {
      appliedFilters += 1
    }
    return appliedFilters
  }, [locationId, groupId, deviceId, hasAutomation])

  const handleClearFilters = () => {
    setGroupId(null)
    setDeviceId(null)
    setLocationId(null)
  }

  const debouncedChangeHandler = debounce((value) => {
    setSearchInput(value)
  }, 300)

  const handleOpenModal = (device: any, param: any) => {
    setDeviceAutomation(device)
    setDeviceAutomationParam(param)
    setIsModalOpen(true)
  }

  const handleInputChange = (event) => {
    const value = event.target.value
    debouncedChangeHandler(value)
  }

  const handleTabChange = (newIndex: number) => {
    setIndex(newIndex)
    setDetailsActive(false)
    setSourceTab(null)
  }

  const handleTabClick = (clickedIndex: number) => {
    if (clickedIndex === index && detailsActive) {
      setDetailsActive(false)
      setSourceTab(null)
    }
  }

  const componentMap = {
    0: Device,
    1: Groups,
  }

  const props = {
    querySearchParams,
    locationId,
    deviceId,
    groupId,
    searchInput,
    hasAutomation,
    handleOpenModal,
  }

  const getComponentProps = (index) => {
    const baseProps = { ...props }

    switch (index) {
      case 0:
        return { ...baseProps }
      case 1:
        return { ...baseProps }
      default:
        return baseProps
    }
  }

  const DetailsComponent = useMemo(() => {
    if (sourceTab === 0) return DeviceAutomations
    if (sourceTab === 1) return GroupAutomations
    return null
  }, [sourceTab])

  const MainComponent = componentMap[index]

  const ActiveComponent = detailsActive ? DetailsComponent : MainComponent
  const activeComponentProps = detailsActive
    ? getComponentProps(sourceTab)
    : getComponentProps(index)

  return (
    <>
      <Header title="Automations" />
      <Flex justify="space-between" align="center">
        <CustomTabs
          tabs={[{ label: 'Devices' }, { label: 'Groups' }]}
          activeIndex={index}
          setActiveIndex={handleTabChange}
          handleClearFilters={handleClearFilters}
          onTabClick={handleTabClick}
        />
        <Flex alignItems="center" justifyContent="center" marginBottom="16px">
          <Tooltip
            label={`${detailsActive ? 'Hide' : 'Show'}  ${index === 0 ? 'Device' : 'Group'
              } Automations`}
            hasArrow
            placement="top"
          >
            <Button
              variant="ghost"
              _focus={{ boxShadow: 'none' }}
              color={detailsActive ? 'blue.500' : 'icon.primary'}
              onClick={() => {
                if (detailsActive && sourceTab === index) {
                  setDetailsActive(false)
                  setSourceTab(null)
                } else {
                  setDetailsActive(true)
                  setSourceTab(index)
                }
              }}
            >
              <TableIcon
                boxSize="20px"
                color={detailsActive ? 'blue.500' : 'icon.primary'}
              />
            </Button>
          </Tooltip>
          <Tooltip label="Filters" hasArrow placement="top">
            <Button
              variant="ghost"
              color="icon.primary"
              onClick={() => setIsFilterModalOpen(true)}
            >
              <FilterIcon boxSize="24px" color="icon.primary" />
              {filtersCounter ? (
                <Badge
                  position="absolute"
                  top="0"
                  right="0"
                  transform="translate(-20%, 5%)"
                  bg="blue.500"
                  color="white"
                  borderRadius="full"
                  px="6px"
                  py="2px"
                  fontSize="12px"
                >
                  {filtersCounter}
                </Badge>
              ) : null}
            </Button>
          </Tooltip>
          <InputGroup>
            <InputLeftElement pointerEvents="none">
              <Search color="#9B9B9B" />
            </InputLeftElement>
            <Input
              type="text"
              placeholder="Search"
              onChange={handleInputChange}
              bg="white"
              border="1px solid transparent"
              borderRadius="full"
              _placeholder={{ color: 'gray.400' }}
              _focus={{
                boxShadow: '0 0 0 1px text.dark',
                border: '1px solid',
                borderColor: 'text.dark',
              }}
            />
          </InputGroup>
        </Flex>
      </Flex>
      <FilterModal
        isOpen={isFilterModalOpen}
        handleClose={() => setIsFilterModalOpen(false)}
        groupId={groupId}
        deviceId={deviceId}
        locationId={locationId}
        hasAutomation={hasAutomation}
        setGroupId={setGroupId}
        setDeviceId={setDeviceId}
        setLocationId={setLocationId}
        setHasAutomation={setHasAutomation}
      />
      <RegisterAutomationModal
        isOpen={isModalOpen}
        deviceToControl={deviceAutomation}
        deviceParam={deviceAutomationParam}
        automationType={index === 0 ? 'device' : 'group'}
        createAutomation={index === 0 ? createDeviceAutomation : createGroupAutomation}
        updateAutomation={index === 0 ? updateDeviceAutomation : null}
        handleClose={() => {
          setIsModalOpen(false)
          setDeviceAutomation(null)
        }}
      />
      {ActiveComponent && <ActiveComponent {...activeComponentProps} />}
    </>
  )
}

export default Automations
